/**
 * Created by Ross on 18/07/2017.
 */
import { NgClass, NgIf } from '@angular/common';
import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute } from '@angular/router';
import { Subscription, timer } from 'rxjs';
import { DataRulesConfirmDialogComponent } from '../data-rules/data-rules-confirm-dialog/data-rules-confirm-dialog.component';
import { RippleDirective } from '../ripple.directive';
import { MessageNotificationService } from '../shared/services/message-notification.service';
import { CandidateOptionsComponent } from './candidate-options/candidate-options.component';
import { DataCleanerService } from './data-cleaner.service';
import { IColumns } from './models/columns-model';
import { DataCleanerSearch, IGroupedSearches, SearchStatus } from './models/data-cleaner-search.model';
import { ISearchResults } from './models/search-results.model';
import { PreviousSearchesBurgerComponent } from './previous-searches-burger/previous-searches-burger.component';
import { PreviousSearchesBurgerService } from './previous-searches-burger/previous-searches-burger.service';
import { ResultsTableComponent } from './results-table/results-table.component';

@Component({
  selector: 'app-data-cleaner',
  templateUrl: './data-cleaner.component.html',
  imports: [
    PreviousSearchesBurgerComponent,
    CandidateOptionsComponent,
    ResultsTableComponent,
    NgClass,
    MatTooltip,
    RippleDirective,
    NgIf
  ],
  styleUrls: ['./data-cleaner.component.scss']
})
export class DataCleanerComponent implements AfterViewInit {
  @ViewChild(CandidateOptionsComponent)
  candidateOptions: CandidateOptionsComponent;
  searchResults: ISearchResults;
  currentSearchId: string;
  isLoading = true;
  isLoadingResults = true;
  dateLastCommentControl;
  cleanOptionsForm;
  hotListName: string;
  isCandidate = true;
  isScheduled = false;
  showResultsTable = false;
  showFilterOptions = true;
  searches: DataCleanerSearch[] = [];
  groupedSearches: IGroupedSearches;
  timerSubscription: Subscription;
  columns: IColumns[] = [
    { label: 'Group', name: 'group' },
    { label: '< 1', name: 'lessOne' },
    { label: '1 - 2', name: 'oneToTwo' },
    { label: '2 - 3', name: 'twoToThree' },
    { label: '3 - 5', name: 'threeToFive' },
    { label: '5 - 8', name: 'fiveToEight' },
    { label: '8+', name: 'eightPlus' },
    { label: 'Never', name: 'never' },
    { label: 'Total', name: 'total' },
  ];
  config = {
    sorting: true,
    ordering: true,
    resizing: true
  };

  constructor(private route: ActivatedRoute,
              public dataCleanerService: DataCleanerService,
              private previousSearchesBurgerService: PreviousSearchesBurgerService,
              private dialog: MatDialog,
              private messageNotificationService: MessageNotificationService
  ) {
    this.dateLastCommentControl = new FormControl({ key: 'dateLastComment', label: '' });
    this.cleanOptionsForm = new FormGroup({ dateLastComment: this.dateLastCommentControl });
    this.loadSearchesForCorporation();
  }

  ngAfterViewInit(): void {
    // TODO: they've created a variable that just is the child component in order to call methods on it.
    //  Change this. Just pass the data it needs down to the options component:
    //  - columns but without the 'group' row
    //  - figure out if that filtering is even necessary
    this.candidateOptions.setPeopleGroups(this.columns);
  }

  changeEntityType(isCandidate: boolean) {
    if (this.isCandidate === isCandidate) return;

    this.isCandidate = isCandidate;
    this.showResultsTable = false;
    this.showFilterOptions = true;
    this.calculateTotal();
  }

  calculateTotal() {
    this.candidateOptions.isCandidate = this.isCandidate;
    this.candidateOptions.calculateTotal();
  }

  loadSearchesForCorporation() {
    this.dataCleanerService.loadSearchesForCorporation().subscribe((searches: DataCleanerSearch[]) => {
      this.searches = searches;
      this.groupAndSortSearches(searches);
    });
  }

  groupAndSortSearches(previousSearches: DataCleanerSearch[]): void {
    this.groupedSearches = this.groupSearchesByStatus(previousSearches);
    this.groupedSearches.COMPLETED?.sort((a, b) =>
        this.compareValues(a.completedTime, b.completedTime) ? -1 : 1);
    this.groupedSearches.RUNNING?.sort((a, b) =>
        this.compareValues(a.searchName, b.searchName) ? 1 : -1);
    this.groupedSearches.QUEUED?.sort((a, b) =>
        this.compareValues(a.queuedTime, b.queuedTime) ? -1 : 1);
    this.groupedSearches.SCHEDULED?.sort((a, b) =>
        this.compareValues(a.searchName, b.searchName) ? 1 : -1);
    this.groupedSearches.FAILED?.sort((a, b) =>
        this.compareValues(a.queuedTime, b.queuedTime) ? -1 : 1);
  }

  private groupSearchesByStatus(searches: DataCleanerSearch[]): IGroupedSearches {
    return searches.reduce((groupedResult: IGroupedSearches, search) => {
      const status = search.searchStatus;
      if (!groupedResult[status]) {
        groupedResult[status] = [];
      }
      groupedResult[status].push(search);
      return groupedResult;
    }, {} as IGroupedSearches);
  }

  compareValues(a: any, b: any): boolean {
    if (!b || a > b) {
      return true;
    } else if (!a || a <= b) {
      return false;
    }
  }

  selectSearch(search: DataCleanerSearch) {
    this.currentSearchId = search.id;
    this.isScheduled = (search.searchStatus === SearchStatus.SEARCH_SCHEDULED);
    this.loadReport(search.id);
  }

  getSearchById(id: string): DataCleanerSearch {
    return this.searches.find((s: DataCleanerSearch) => {
      return s.id === id;
    });
  }

  removeSearchById(id) {
    const index = this.searches.findIndex(aSearch => aSearch.id === id);
    this.searches.splice(index, 1);
    this.groupAndSortSearches(this.searches);
  }

  loadReport(id: string) {
    this.isLoading = true;
    this.isLoadingResults = true;
    const search: DataCleanerSearch = this.getSearchById(id);
    if ((search.searchStatus === SearchStatus.SEARCH_SCHEDULED) ||
        (search.searchStatus === SearchStatus.SEARCH_RUNNING) ||
        (search.searchStatus === SearchStatus.SEARCH_FAILED)) {
      this.showResultsTable = false;
      this.showFilterOptions = true;
      this.candidateOptions.cleanOptionsForm.markAsPristine();
    } else {
      this.showResultsTable = true;
      this.showFilterOptions = false;
    }
    this.dataCleanerService.loadSearchResults(id).subscribe((results: ISearchResults) => {
      this.searchResults = results;
      this.isCandidate = (search.entity.toUpperCase() !== 'CONTACT');
      // TODO: This is so stupid. This SHOULD NOT be calling methods on the child component and passing the search down this way.
      //  This is spaghetti. It should be passing the data down to the child via @Input
      //  I want to fix this but I don't have time.
      this.candidateOptions.populateSearch(search);
      this.calculateTotal();
      if (results.complete) {
        this.isLoading = false;
        this.isLoadingResults = false;
      } else if (!(search.searchStatus === SearchStatus.SEARCH_SCHEDULED || search.searchStatus === SearchStatus.SEARCH_FAILED)) {
        // TODO: this needs to hook into the timer/event to recheck periodically.
        this.setupReportCallback(id);
      } else {
        this.isLoading = false;
      }
    }, error => {
      this.isLoading = false;
      this.messageNotificationService.error('There was a problem loading this search, please contact support', error.message);
      console.log(error);
    });
  }

  removeSearch(search) {
    const dialogRef = this.dialog.open(DataRulesConfirmDialogComponent, {
      width: '45vw',
      height: 'auto',
      autoFocus: true,
      data: {
        titleIcon: 'bhi-delete-o',
        title: 'Delete Scheduled Search',
        messageIcon: 'bhi-search',
        message: 'Are you sure you want to delete the scheduled search - ' + search.searchName
      }
    });
    dialogRef.afterClosed().subscribe((selection) => {
      if (selection) {
        this.dataCleanerService.deleteScheduledSearch(search.id).subscribe((data: any) => {
          if (data.success) {
            this.messageNotificationService.success('Search "' + data.name + '" was successfully deleted');
          }
          if (this.currentSearchId === search.id) {
            this.candidateOptions.resetSearch();
          }
          this.removeSearchById(search.id);
        }, (error) => {
          this.messageNotificationService.error('An internal error occurred', error.message);
          this.isLoading = false;
          console.log(error);
        });
      }
    });
  }

  runSearch(postObject: any) {
    this.isLoading = true;
    this.isLoadingResults = true;
    this.dataCleanerService.prepareSearch(postObject).subscribe((data: any) => {
      if (!data.canRun) {
        alert('There is currently a report running. Please try again later.');
      } else {
        this.currentSearchId = data.searchId;
        this.setupReportCallback(data.searchId);
        this.showResultsTable = true;
        this.showFilterOptions = false;
        this.loadSearchesForCorporation();
      }
    }, (err) => {
      alert('An internal error occurred.');
      console.log(err);
    }, () => {
      this.isLoading = false;
    });
  }

  setupReportCallback(searchId) {
    this.currentSearchId = searchId;
    this.isLoading = true;
    this.timerSubscription = timer(0, 3000).subscribe((t: any) => {
      this.dataCleanerService.getSearchResults(searchId).subscribe((results: any) => {
        this.searchResults = results;
        if (results.complete) {
          this.loadSearchesForCorporation();
          this.timerSubscription.unsubscribe();
          this.isLoadingResults = false;
        } else if (results.error) {
          this.messageNotificationService.error('Sorry, there was a problem running this search. If this issue persists, please contact support');
          this.timerSubscription.unsubscribe();
          this.isLoadingResults = false;
        }
      }, err => {
        this.timerSubscription.unsubscribe();
        console.log(err);
        this.isLoadingResults = false;
      }, () => {
        this.isLoading = false;
      });
    });
  }

  handleSearchIdChange(newId: string) {
    this.currentSearchId = newId;
  }

  onScheduledSearch() {
    this.loadSearchesForCorporation();
  }

  closeSearchBurger() {
    this.previousSearchesBurgerService.closePreviousSearchesBurger();
  }
}
